import { createContext, PropsWithChildren, useContext, useState } from 'react';

import { HierarchicalCategoriesQuery } from '~/gql/graphql';
import { CustomerAddress, CustomerFormValues, MarketplaceChannel, PremarketplaceJourney } from '~/types/marketplace';

// Mirrors the attachment items accepted by the enterprise lead API; `created`
// is an ISO string because it round-trips through FormData.
// The two ways out of the education step: on to vendors, or to the nurture form.
export type EducationChoice = 'connect' | 'not-ready';

export type BillAttachment = {
  documentId: string;
  fileName: string;
  fileSize: number;
  s3Url: string;
  created: string;
};

export type PremarketplaceFormDataSchema = {
  selectedAddress: CustomerAddress;
  callTimePreference: string;
  selectedCategorySlugs: string[];
  selectedBundleCategorySlug?: string;
  selectedVendorIds: string[];
  isManualVendorMatching?: boolean;
  intent: string;
  selectedTopics: string[];
  // Labels for `selectedTopics`, carried alongside the values so the lead notes read as prose
  // without the payload builder needing to resolve config-driven content.
  selectedTopicLabels?: string[];
  // Free text from the topics step's "Tell us more" field; prefills the not-ready question.
  topicsNote?: string;
  educationChoice?: EducationChoice;
  // Used by the connect-with-vendors branch to send buyers who defer vendor contact
  // through the support pathway instead.
  isNotReadyToConnectWithVendors?: boolean;
  purchasePriority: string;
  contact: Partial<CustomerFormValues>;
  householdSize: number;
  finance: boolean;
  journey?: PremarketplaceJourney;
  billAttachment?: BillAttachment;
};

export type Category = NonNullable<
  NonNullable<HierarchicalCategoriesQuery['hierarchicalCategories']>[number]['subCategories']
>[number];

type PremarketplaceProviderProps = PropsWithChildren & {
  channel?: MarketplaceChannel;
  categories: Category[];
};

type PremarketplaceContextType = {
  categories: Category[];
  formData?: Partial<PremarketplaceFormDataSchema>;
  setFormData: (data: Partial<PremarketplaceFormDataSchema>) => void;
};

const PremarketplaceContext = createContext<PremarketplaceContextType | undefined>(undefined);

export const PremarketplaceProvider = ({ categories, children }: PremarketplaceProviderProps) => {
  const [formData, _setFormData] = useState<Partial<PremarketplaceFormDataSchema>>({});

  const setFormData = (data: Partial<PremarketplaceFormDataSchema>) => {
    _setFormData((prev) => ({ ...prev, ...data }));
  };

  return (
    <PremarketplaceContext.Provider value={{ categories, formData, setFormData }}>
      {children}
    </PremarketplaceContext.Provider>
  );
};

export const usePremarketplaceContext = () => {
  const context = useContext(PremarketplaceContext);
  if (!context) {
    throw new Error('usePremarketplaceContext must be used within PremarketplaceProvider');
  }
  return context;
};
